import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { agencyApi, apiErr } from "./agencyApi";
import { Logo } from "@/components/Logo";
import "./agency.css";

const ART = {
  login: { img: "shot8", headline: "Where NJM and its partners build together.", sub: "Your productions, crew and documents, one calm, secure workspace." },
  forgot: { img: "shot5", headline: "We'll get you back in.", sub: "Reset your password securely and pick up where you left off." },
  set: { img: "shot15", headline: "Welcome to the partnership.", sub: "Set a password to access your NJM Productions Agency Portal." },
  reset: { img: "shot5", headline: "Choose a new password.", sub: "A fresh, secure password for your agency workspace." },
};

function strength(pw) {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(s, 4);
}
const STRENGTH_LABEL = ["", "Weak", "Fair", "Good", "Strong"];

export default function AgencyAuth() {
  const nav = useNavigate();
  const loc = useLocation();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";

  const mode = loc.pathname.includes("/set-password") ? "set"
    : loc.pathname.includes("/reset-password") ? "reset"
      : loc.pathname.includes("/forgot") ? "forgot" : "login";
  const kind = mode === "reset" ? "reset" : "set";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [stage, setStage] = useState("form"); // form | sent | done | expired
  const [tokenChecked, setTokenChecked] = useState(mode === "set" || mode === "reset" ? false : true);

  const art = ART[mode];
  const pwScore = useMemo(() => strength(password), [password]);

  useEffect(() => {
    setErr(""); setStage("form"); setPassword(""); setConfirm(""); setShowPw(false);
    if (mode === "set" || mode === "reset") {
      setTokenChecked(false);
      if (!token) { setStage("expired"); setTokenChecked(true); return; }
      agencyApi.tokenStatus(token, kind)
        .then((r) => { if (!r.data.valid) setStage("expired"); })
        .catch(() => setStage("expired"))
        .finally(() => setTokenChecked(true));
    }
  }, [mode, token, kind]);

  const submitLogin = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await agencyApi.login(email.trim().toLowerCase(), password);
      localStorage.setItem("njm_last_portal", "agency"); // 2026-09 security fix — see AuthContext.js mount effect
      window.location.href = "/agency"; // hard nav so AuthContext re-reads the new session cookie
    } catch (ex) { setErr(apiErr(ex)); setBusy(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { await agencyApi.forgot(email.trim().toLowerCase()); setStage("sent"); }
    catch (ex) { setErr(apiErr(ex)); }
    finally { setBusy(false); }
  };

  const submitSet = async (e) => {
    e.preventDefault(); setErr("");
    if (pwScore < 3) { setErr("Please choose a stronger password (min 8 characters, mixing letters, numbers and symbols)."); return; }
    if (password !== confirm) { setErr("Those passwords don't match."); return; }
    setBusy(true);
    try { await agencyApi.setPassword(token, password, kind); setStage("done"); }
    catch (ex) {
      const msg = apiErr(ex);
      if (/expired/i.test(msg)) setStage("expired"); else setErr(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="ag-root">
      <div className="ag-auth">
        <div className="ag-auth-art">
          <picture>
            <source srcSet={`/agency/${art.img}_1600.webp`} media="(min-width: 900px)" />
            <img className="ag-auth-art-bg" src={`/agency/${art.img}_800.webp`} alt="" aria-hidden="true" loading="eager" />
          </picture>
          <div className="ag-auth-art-inner">
            <div className="ag-brand-lockup-dark">
              <Logo theme="dark" iconOnly size="xl" testId="agency-auth-logo" />
              <span className="ag-brand-text on-dark" style={{ fontSize: 16, letterSpacing: ".02em" }}>NJM Productions</span>
            </div>
            <div>
              <h2 className="ag-auth-art-headline">{art.headline}</h2>
              <p className="ag-auth-art-sub">{art.sub}</p>
            </div>
          </div>
        </div>

        <div className="ag-auth-form-wrap">
          <div className="ag-auth-form ag-entrance">
            {err && <div className="ag-alert ag-alert-err" role="alert" data-testid="agency-auth-error">{err}</div>}

            {/* ---------------- LOGIN ---------------- */}
            {mode === "login" && (
              <form onSubmit={submitLogin} noValidate>
                <h1 className="ag-h1">Welcome back</h1>
                <p className="ag-auth-form-sub">Sign in to your NJM Productions Agency Portal.</p>
                <label className="ag-field">
                  <span className="ag-label">Email</span>
                  <input className="ag-input" type="email" autoComplete="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)} placeholder="you@agency.com" data-testid="agency-login-email" />
                </label>
                <div className="ag-field">
                  <div className="ag-pw-row">
                    <span className="ag-label">Password</span>
                    <button type="button" className="ag-pw-toggle" onClick={() => setShowPw((s) => !s)} data-testid="agency-login-pw-toggle">{showPw ? "HIDE" : "SHOW"}</button>
                  </div>
                  <input className="ag-input" type={showPw ? "text" : "password"} autoComplete="current-password" required value={password}
                    onChange={(e) => setPassword(e.target.value)} placeholder="••••••••••••" data-testid="agency-login-password" />
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 22 }}>
                  <button type="button" className="ag-link" onClick={() => nav("/agency/forgot-password")} data-testid="agency-forgot-link">Forgot password?</button>
                </div>
                <button className="ag-btn ag-btn-gold" style={{ width: "100%", padding: 17 }} disabled={busy} data-testid="agency-login-submit">{busy ? "Signing in…" : "Log in"}</button>
              </form>
            )}

            {/* ---------------- FORGOT ---------------- */}
            {mode === "forgot" && stage === "form" && (
              <form onSubmit={submitForgot} noValidate>
                <button type="button" className="ag-link" style={{ marginBottom: 24, display: "block" }} onClick={() => nav("/agency/login")}>← Back to log in</button>
                <h1 className="ag-h1">Reset your password</h1>
                <p className="ag-auth-form-sub">Enter your email and we'll send you a secure link to reset it.</p>
                <label className="ag-field">
                  <span className="ag-label">Email</span>
                  <input className="ag-input" type="email" autoComplete="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)} placeholder="you@agency.com" data-testid="agency-forgot-email" />
                </label>
                <button className="ag-btn ag-btn-gold" style={{ width: "100%", padding: 17 }} disabled={busy} data-testid="agency-forgot-submit">{busy ? "Sending…" : "Send reset link"}</button>
              </form>
            )}
            {mode === "forgot" && stage === "sent" && (
              <div data-testid="agency-forgot-sent">
                <h1 className="ag-h1">Check your inbox</h1>
                <p className="ag-auth-form-sub">If an account exists for that email, a reset link is on its way. The link is valid for 1 hour.</p>
                <div style={{ display: "flex", gap: 12 }}>
                  <button className="ag-btn ag-btn-dark" style={{ flex: 1 }} onClick={() => nav("/agency/login")}>Back to log in</button>
                  <button className="ag-btn ag-btn-ghost" onClick={submitForgot} disabled={busy}>Resend</button>
                </div>
              </div>
            )}

            {/* ---------------- SET / RESET ---------------- */}
            {(mode === "set" || mode === "reset") && !tokenChecked && <p className="ag-small">Checking your link…</p>}
            {(mode === "set" || mode === "reset") && tokenChecked && stage === "form" && (
              <form onSubmit={submitSet} noValidate>
                <h1 className="ag-h1">{mode === "reset" ? "Choose a new password" : "Set your password"}</h1>
                <p className="ag-auth-form-sub">{mode === "reset" ? "Pick a new, secure password for your workspace." : "Welcome to NJM Productions. Choose a secure password to finish setting up your account."}</p>
                <div className="ag-field">
                  <div className="ag-pw-row">
                    <span className="ag-label">New password</span>
                    <button type="button" className="ag-pw-toggle" onClick={() => setShowPw((s) => !s)}>{showPw ? "HIDE" : "SHOW"}</button>
                  </div>
                  <input className="ag-input" type={showPw ? "text" : "password"} autoComplete="new-password" required value={password}
                    onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" data-testid="agency-set-password" />
                  <div className={`ag-strength s${pwScore}`}><span /><span /><span /><span /></div>
                  <span className="ag-strength-label">{password ? STRENGTH_LABEL[pwScore] : "Use 8+ characters, with a mix of letters, numbers & symbols"}</span>
                </div>
                <label className="ag-field">
                  <span className="ag-label">Confirm password</span>
                  <input className="ag-input" type={showPw ? "text" : "password"} autoComplete="new-password" required value={confirm}
                    onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter your password" data-testid="agency-set-confirm" />
                </label>
                <button className="ag-btn ag-btn-gold" style={{ width: "100%", padding: 17 }} disabled={busy} data-testid="agency-set-submit">{busy ? "Saving…" : (mode === "reset" ? "Reset password" : "Set password & continue")}</button>
              </form>
            )}
            {(mode === "set" || mode === "reset") && stage === "done" && (
              <div data-testid="agency-set-done">
                <h1 className="ag-h1">You're all set</h1>
                <p className="ag-auth-form-sub">Your password has been saved and you're signed in.</p>
                <button className="ag-btn ag-btn-gold" style={{ width: "100%", padding: 17 }} onClick={() => { localStorage.setItem("njm_last_portal", "agency"); window.location.href = "/agency"; }} data-testid="agency-set-continue">Continue to the portal</button>
              </div>
            )}
            {(mode === "set" || mode === "reset") && stage === "expired" && (
              <div data-testid="agency-link-expired">
                <h1 className="ag-h1">This link has expired</h1>
                <p className="ag-auth-form-sub">For your security, this link is single-use and time-limited. Request a fresh one to continue.</p>
                <button className="ag-btn ag-btn-gold" style={{ width: "100%", padding: 17, marginBottom: 12 }} onClick={() => nav("/agency/forgot-password")}>Request a new link</button>
                <button className="ag-btn ag-btn-ghost" style={{ width: "100%" }} onClick={() => nav("/agency/login")}>Back to log in</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
